import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import {
  ArrowRight, Wind, Droplets, Wrench, Cog, ChevronRight,
  Star, ShieldCheck, Clock, Award, Phone, CheckCircle2, MapPin, ArrowUpRight,
} from "lucide-react";
import CTASection from "../../components/public/CTASection";
import ContactForm from "../../components/public/ContactForm";
import { useSettings } from "../../lib/useSettings";

const iconMap = { Wind, Droplets, Wrench, Cog };

export function ServicesListPage() {
  const { settings: site } = useSettings();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/services").then(r => setItems(r.data)); }, []);
  return (
    <>
      <title>{`Nos services${site?.company_name ? " | " + site.company_name : ""}`}</title>
      <meta name="description" content={`Découvrez tous nos services${site?.main_city ? " à " + site.main_city : ""}${site?.intervention_radius_km ? " et dans un rayon de " + site.intervention_radius_km + " km" : ""}.`}/>
      <section className="pt-14 pb-10 bg-[color:var(--brand-navy)] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <nav className="text-xs text-white/60 mb-4 flex items-center gap-2">
            <Link to="/">Accueil</Link> <ChevronRight className="w-3 h-3"/> Services
          </nav>
          <h1 className="font-display text-4xl md:text-6xl font-black">Nos services</h1>
          <p className="mt-4 text-white/80 max-w-2xl">Plomberie, climatisation, dépannage 6J/7 — une expertise complète pour votre confort.</p>
        </div>
      </section>
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((s) => {
            const Icon = iconMap[s.icon] || Wrench;
            return (
              <Link key={s.id} to={`/services/${s.slug}`} className="group rounded-3xl bg-white border border-[hsl(var(--border))] overflow-hidden shadow-hover" data-testid={`services-list-${s.slug}`}>
                {s.image_url && <div className="relative h-48 overflow-hidden"><img src={s.image_url} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/></div>}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-[color:var(--brand-sky)] mb-2 text-xs uppercase font-bold tracking-wider"><Icon className="w-4 h-4"/> {s.category}</div>
                  <h3 className="font-display text-xl font-bold text-[color:var(--brand-navy)] mb-2">{s.title}</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] line-clamp-3">{s.short_description}</p>
                  <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-[color:var(--brand-navy)] group-hover:text-[color:var(--brand-red)]">En savoir plus <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition"/></div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
      <CTASection/>
    </>
  );
}

export function ServiceDetailPage() {
  const { settings: site } = useSettings();
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [realisations, setRealisations] = useState([]);

  useEffect(() => {
    api.get(`/services/${slug}`).then(r => setS(r.data)).catch(() => setS(false));
  }, [slug]);

  useEffect(() => {
    if (!s) return;
    api.get("/realizations").then(r => {
      const kw = (s.keyword || s.title || "").toLowerCase();
      const rel = (r.data || []).filter((x) => {
        const haystack = `${x.title || ""} ${x.category || ""} ${x.description || ""}`.toLowerCase();
        return kw.split(/\s+/).some((w) => w && w.length > 3 && haystack.includes(w));
      }).slice(0, 6);
      setRealisations(rel);
    }).catch(() => {});
  }, [s]);

  if (s === false) return (
    <div className="py-24 text-center">
      Service introuvable. <Link to="/services" className="underline">Retour aux services</Link>
    </div>
  );
  if (!s) return <div className="py-24 text-center">Chargement...</div>;

  const Icon = iconMap[s.icon] || Wrench;
  const radiusKm = site?.intervention_radius_km || 40;
  const mainCity = site?.main_city || "votre ville";
  const trustBadges = [
    { icon: Award, label: "Expérience", desc: "Certifiés & assurés" },
    { icon: Clock, label: "Devis sous 24h", desc: "Réponse rapide garantie" },
    { icon: ShieldCheck, label: "Garantie décennale", desc: "Travaux couverts 10 ans" },
    { icon: MapPin, label: `${radiusKm} km autour de ${mainCity}`, desc: "Intervention locale" },
  ];

  // Extract bullet-style points from the long description (lines starting with - or •)
  // and keep the remaining paragraphs separately.
  const raw = (s.long_description || "").trim();
  const lines = raw.split(/\r?\n/);
  const bullets = [];
  const paragraphs = [];
  for (const line of lines) {
    const t = line.trim();
    if (!t) continue;
    if (/^[-•·*]\s+/.test(t)) bullets.push(t.replace(/^[-•·*]\s+/, ""));
    else paragraphs.push(t);
  }

  return (
    <>
      <title>{s.meta_title || s.title}</title>
      <meta name="description" content={s.meta_description} />

      {/* HERO — photo prominently shown, not hidden behind 30% opacity */}
      <section className="relative pt-10 pb-16 md:pt-14 md:pb-20 bg-[color:var(--brand-navy)] text-white overflow-hidden">
        {s.image_url && (
          <>
            <img src={s.image_url} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--brand-navy)]/95 via-[color:var(--brand-navy)]/80 to-[color:var(--brand-navy)]/40" />
          </>
        )}
        {!s.image_url && <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--brand-navy)] to-[#0f223c]" />}
        <div className="relative max-w-7xl mx-auto px-4 md:px-8">
          <nav className="text-xs text-white/70 mb-5 flex items-center gap-2">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/services" className="hover:text-white">Services</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{s.category || s.title}</span>
          </nav>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[color:var(--brand-sky)] text-white text-[10px] font-black uppercase tracking-wider mb-5 shadow-lg">
            <Icon className="w-3.5 h-3.5" />
            {s.category || "Service"}
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black leading-[0.98] max-w-3xl" data-testid="service-title">
            {s.title}
          </h1>
          <p className="mt-4 text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed">{s.short_description}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/devis" className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90 text-white font-bold shadow-xl transition-all hover:-translate-y-0.5" data-testid="service-cta-devis">
              Demander un devis gratuit <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:0761664961" className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-white/10 border border-white/30 hover:bg-white/20 text-white font-bold transition-all backdrop-blur-sm" data-testid="service-cta-phone">
              <Phone className="w-4 h-4" /> 07 61 66 49 61
            </a>
          </div>
        </div>
      </section>

      {/* TRUST BADGES BAR */}
      <section className="bg-white border-b border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {trustBadges.map((b) => (
            <div key={b.label} className="flex items-start gap-3" data-testid={`trust-${b.label}`}>
              <div className="w-10 h-10 rounded-xl bg-[color:var(--brand-sky)]/10 text-[color:var(--brand-sky)] flex items-center justify-center flex-shrink-0">
                <b.icon className="w-5 h-5" strokeWidth={2.2} />
              </div>
              <div>
                <div className="font-display font-bold text-sm text-[color:var(--brand-navy)] leading-tight">{b.label}</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{b.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="py-14 md:py-20 bg-gradient-to-br from-white to-[hsl(var(--muted))]/40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-[1.5fr_1fr] gap-10 lg:gap-14">
          {/* LEFT — rich content */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-black mb-3">Notre expertise</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[color:var(--brand-navy)] leading-[1.05] mb-6">
              {s.title.replace(/—.*$/, "").trim()} à {mainCity} &amp; {radiusKm} km autour
            </h2>

            {paragraphs.length > 0 && (
              <div className="prose prose-lg max-w-none mb-8">
                {paragraphs.map((p, i) => (
                  <p key={i} className="text-[hsl(var(--foreground))]/90 text-base md:text-lg leading-relaxed mb-4">{p}</p>
                ))}
              </div>
            )}

            {bullets.length > 0 && (
              <div className="mb-8">
                <h3 className="font-display text-xl font-bold text-[color:var(--brand-navy)] mb-4">Ce que nous faisons pour vous</h3>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2.5 p-3.5 rounded-xl bg-white border border-[hsl(var(--border))] shadow-sm">
                      <CheckCircle2 className="w-5 h-5 text-[color:var(--brand-sky)] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[hsl(var(--foreground))] leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* YouTube video (if any) */}
            {s.youtube_url && (
              <div className="aspect-video rounded-2xl overflow-hidden mt-8 border border-[hsl(var(--border))] shadow-xl">
                <iframe src={`https://www.youtube.com/embed/${s.youtube_url}`} className="w-full h-full" allowFullScreen title={s.title} />
              </div>
            )}

            {/* Related realisations */}
            {realisations.length > 0 && (
              <div className="mt-12">
                <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-black mb-1.5">Chantiers similaires</div>
                    <h3 className="font-display text-xl md:text-2xl font-bold text-[color:var(--brand-navy)]">Nos réalisations récentes</h3>
                  </div>
                  <Link to="/realisations" className="text-sm font-semibold text-[color:var(--brand-navy)] hover:text-[color:var(--brand-sky)] inline-flex items-center gap-1">
                    Voir toutes <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {realisations.map((r) => (
                    <Link key={r.id} to={`/realisations/${r.slug}`} className="group relative aspect-[4/3] rounded-xl overflow-hidden bg-[hsl(var(--muted))] shadow-sm hover:shadow-lg transition" data-testid={`related-reali-${r.slug}`}>
                      {(r.cover_url || r.images?.[0]) && (
                        <img src={r.cover_url || r.images?.[0]} alt={r.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--brand-navy)]/85 via-[color:var(--brand-navy)]/30 to-transparent" />
                      <div className="absolute bottom-2 left-2.5 right-2.5 text-white">
                        <div className="text-[9px] uppercase tracking-wider font-black text-white/80 truncate">{r.category || r.zone || "Chantier"}</div>
                        <div className="font-display font-bold text-xs md:text-sm leading-tight line-clamp-2">{r.title}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — sticky sidebar */}
          <aside className="md:sticky md:top-24 h-max space-y-5">
            <ContactForm title="Besoin d'un devis ?" description={`Pour ${s.title.toLowerCase()}, réponse sous 24h.`} />

            {/* Mini review card */}
            <div className="rounded-2xl p-5 bg-gradient-to-br from-[color:var(--brand-navy)] to-[#0f223c] text-white shadow-xl" data-testid="service-reviews-mini">
              <div className="flex items-center gap-1 mb-1.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                ))}
                <span className="ml-1 text-xs font-bold">{(site?.reviews_average || 5).toString().replace(".", ",")} / 5</span>
              </div>
              <p className="text-sm leading-snug text-white/90">
                {site?.reviews_count ? <><strong className="text-white">{site.reviews_count} clients</strong> satisfaits depuis {site?.reviews_since_year || ""}, dans un rayon de {radiusKm} km autour de {mainCity}.</> : <>Des clients satisfaits dans un rayon de {radiusKm} km autour de {mainCity}.</>}
              </p>
              <Link to="/realisations" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[color:var(--brand-sky)] hover:text-white">
                Voir nos chantiers <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <CTASection />
    </>
  );
}
